
import { useState, useEffect } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import { useToast } from "@/hooks/use-toast";
import { CompanyData, Connection } from '@/types';
import { Json } from '@/integrations/supabase/types';
import { useLocation } from 'react-router-dom';
import { parseProducts, parseConnections } from '@/utils/jsonHelpers';
import { normalizeConnectionForStorage } from '@/utils/organization/saveOrganization';
import { generateStableIdentifier } from '@/utils/magic-link/helpers';

export const useFormDrafts = () => {
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [formCompleted, setFormCompleted] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const [pendingSave, setPendingSave] = useState<CompanyData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tokenDetected, setTokenDetected] = useState(false);
  const [isVerifyingToken, setIsVerifyingToken] = useState(false);
  const [disableEin, setDisableEin] = useState(false);
  const { toast } = useToast();
  const location = useLocation();

  const getTokenFromURL = () => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('token');
  };

  const getSubmissionIdentifierFromURL = () => {
    const urlParams = new URLSearchParams(location.search);
    const urlSubmissionId = urlParams.get('submission_identifier');
    // If not in URL, try sessionStorage, otherwise default to 'default'
    return urlSubmissionId || sessionStorage.getItem('figabl_submission_identifier') || 'default';
  };

  const getSpecificOrganizationFromURL = () => {
    const urlParams = new URLSearchParams(location.search);
    const orgId = urlParams.get('org_id') || urlParams.get('organisation_id');
    const draftId = urlParams.get('draft_id');
    const submissionIdentifier = getSubmissionIdentifierFromURL();

    return {
      organisationId: orgId,
      draftId: draftId,
      submissionIdentifier: submissionIdentifier,
      hasSpecificOrg: !!(orgId || draftId)
    };
  };

  const verifyMagicLink = async (token: string) => {
    setIsVerifyingToken(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));

      const { data: authData, error: sessionError } = await supabase.auth.getSession();

      if (sessionError) {
        throw sessionError;
      }

      // Check if this is a specific organization magic link
      const {
        organisationId,
        draftId: specificDraftId,
        hasSpecificOrg,
        submissionIdentifier
      } = getSpecificOrganizationFromURL();

      if (!authData.session) {
        const { data: verifyData, error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: token,
          type: 'magiclink'
        });

        if (verifyError) {
          throw verifyError;
        }

        if (verifyData.session) {
          setSession(verifyData.session);

          // Check for the authenticated user's email in the magic_link_sessions table
          // (either as primary email or alternative email)
          const email = verifyData.session.user.email;

          // First, look up the session by submission_identifier to get the canonical data
          const { data: sessionData, error: sessionError } = await supabase
            .from('magic_link_sessions')
            .select('*')
            .eq('submission_identifier', submissionIdentifier)
            .maybeSingle();

          if (sessionError && sessionError.code !== 'PGRST116') {
            console.error('Error retrieving magic link session:', sessionError);
          }

          // Check if the authenticated email matches either primary or alternative emails
          let primarySessionData = null;
          let altSessionData = null;

          if (sessionData) {
            // Check if email matches primary email
            if (sessionData.email === email) {
              primarySessionData = sessionData;
            } else {
              // Check if email is in alternative emails
              const altEmails = sessionData.alternative_email;
              const isAlternativeEmail = altEmails && (
                (Array.isArray(altEmails) && altEmails.includes(email)) ||
                (typeof altEmails === 'string' && altEmails === email)
              );

              if (isAlternativeEmail) {
                altSessionData = [sessionData]; // Format as array to match RPC response
              }
            }
          }
          // Find the matching session with the right submission identifier
          const hasAlternativeEmailMatch = Array.isArray(altSessionData) && altSessionData.length > 0;
          const alternativeEmailData = hasAlternativeEmailMatch
            ? altSessionData.find(session => session.submission_identifier === submissionIdentifier)
            : null;

          // Determine if this is an alternative email login
          const isAlternativeEmail = !!alternativeEmailData;

          // Load specified organization if present in URL
          if (hasSpecificOrg) {
            if (specificDraftId) {
              await loadDraftById(specificDraftId);
            } else if (organisationId) {
              await loadOrganisationById(organisationId, submissionIdentifier);
            } else if (isAlternativeEmail && alternativeEmailData?.organisation_id) {
              // If user logged in with alternative email, load the organization associated with that email
              await loadOrganisationById(alternativeEmailData.organisation_id, submissionIdentifier);
            } else {
              await loadSavedProgress(
                verifyData.session.user.id,
                verifyData.session.user.email,
                submissionIdentifier
              );
            }
          } else {
            // If using alternative email, check if there's an organization linked to that session
            if (isAlternativeEmail && alternativeEmailData?.organisation_id) {
              await loadOrganisationById(alternativeEmailData.organisation_id, submissionIdentifier);
            } else {
              await loadSavedProgress(
                verifyData.session.user.id,
                verifyData.session.user.email,
                submissionIdentifier
              );
            }
          }

          if (pendingSave) {
            await saveDraft(pendingSave, verifyData.session, submissionIdentifier);
            setPendingSave(null);
          }

          toast({
            title: "Authenticated successfully",
            description: "Your progress has been loaded."
          });

          setIsVerifyingToken(false);
          setTokenDetected(false);
          setIsLoading(false);

          if (window.history.replaceState) {
            // Keep org_id or draft_id in the URL if they exist, but remove the token
            const currentParams = new URLSearchParams(window.location.search);
            currentParams.delete('token');
            const newParams = currentParams.toString();
            const newUrl = window.location.pathname + (newParams ? `?${newParams}` : '');
            window.history.replaceState({}, document.title, newUrl);
          }

          return;
        }

        // Check for various types of tokens in the URL fragment
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const recoveryToken = hashParams.get('recovery_token');
        if (recoveryToken) {
          // Handle password recovery
          // Example: navigate to a password reset page
          return;
        }

        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');

        if (accessToken && refreshToken) {
          // Handle the tokens as needed
          return;
        }
      } else {
        setSession(authData.session);

        // Check if the authenticated email is an alternative email
        const email = authData.session.user.email;

        // Check if the email is an alternative email
        const { data: altSessionData, error: altSessionError } = await (supabase
          .rpc as any)('find_session_by_alternative_email', { search_email: email });

        if (altSessionError) {
          console.error('Error checking alternative email:', altSessionError);
        }

        const hasAlternativeEmailMatch = Array.isArray(altSessionData) && altSessionData.length > 0;
        const alternativeEmailData = hasAlternativeEmailMatch
          ? altSessionData.find(session => session.submission_identifier === submissionIdentifier)
          : null;

        // Load specified organization if present in URL
        if (hasSpecificOrg) {
          if (specificDraftId) {
            await loadDraftById(specificDraftId);
          } else if (organisationId) {
            await loadOrganisationById(organisationId, submissionIdentifier);
          } else if (alternativeEmailData?.organisation_id) {
            // If user logged in with alternative email, load the organization associated with that email
            await loadOrganisationById(alternativeEmailData.organisation_id, submissionIdentifier);
          } else {
            await loadSavedProgress(
              authData.session.user.id,
              authData.session.user.email,
              submissionIdentifier
            );
          }
        } else {
          // If using alternative email, check if there's an organization linked to that session
          if (alternativeEmailData?.organisation_id) {
            await loadOrganisationById(alternativeEmailData.organisation_id, submissionIdentifier);
          } else {
            await loadSavedProgress(
              authData.session.user.id,
              authData.session.user.email,
              submissionIdentifier
            );
          }
        }

        if (pendingSave) {
          await saveDraft(pendingSave, authData.session, submissionIdentifier);
          setPendingSave(null);
        }
      }

      toast({
        title: "Authenticated successfully",
        description: "Your progress has been loaded."
      });
    } catch (error: any) {
      toast({
        title: "Authentication error",
        description: error.message || "Failed to process magic link",
        variant: "destructive",
      });
    } finally {
      setIsVerifyingToken(false);
      setTokenDetected(false);
      setIsLoading(false);

      if (window.history.replaceState) {
        // Keep org_id or draft_id in the URL if they exist, but remove the token
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.delete('token');
        const newParams = currentParams.toString();
        const newUrl = window.location.pathname + (newParams ? `?${newParams}` : '');
        window.history.replaceState({}, document.title, newUrl);
      }
    }
  };

  // Add new function to load an organization by ID
  const loadOrganisationById = async (organisationId: string, submissionIdentifier: string = 'default') => {
    try {
      setIsLoading(true);

      // Check if this org is linked to a specific submission
      const { data: sessionData } = await supabase
        .from('magic_link_sessions')
        .select('*')
        .eq('organisation_id', organisationId)
        .eq('submission_identifier', submissionIdentifier)
        .maybeSingle();

      const { data: org, error } = await supabase
        .from('organisations')
        .select('*')
        .eq('id', organisationId)
        .single();

      if (error) {
        throw error;
      }

      if (org) {
        // Format organization data to match CompanyData structure
        const formatOrg: CompanyData = {
          user_id: org.user_id,
          pocName: org.poc_name,
          pocEmail: org.poc_email,
          pocPhone: org.poc_phone || '',
          pocPhoneVerified: org.poc_phone_verified || false,
          name: org.name,
          name_dbas: org.name_dbas || '',
          address: org.address,
          stateOfIncorporation: org.state_of_incorporation,
          ein: org.ein,
          revenueLast12Months: org.revenue_last_12_months || undefined,
          products: parseProducts(org.products),
          connections: parseConnections(org.connections),
          // Store the organization_id so we know we're editing an existing entry
          organization_id: organisationId
        };

        // Add connections to local storage
        if (org.connections) {
          sessionStorage.removeItem('figabl_connection_ids');
          sessionStorage.removeItem('figabl_connection_id');
          sessionStorage.removeItem('figabl_csv_connection_id');
          sessionStorage.removeItem('figabl_csv_connection_ids');

          if (formatOrg.connections.length > 0) {
            const connectionIds = formatOrg.connections.map(conn => conn.id);

            const csvConnectionIds = connectionIds.filter(id => id.startsWith('csv-import-'));
            const nonCsvConnectionIds = connectionIds.filter(id => !id.startsWith('csv-import-'));

            if (nonCsvConnectionIds.length > 0) {
              sessionStorage.setItem('figabl_connection_ids', JSON.stringify(nonCsvConnectionIds));
              sessionStorage.setItem('figabl_connection_id', nonCsvConnectionIds[0]);
            }

            if (csvConnectionIds.length > 0) {
              sessionStorage.setItem('figabl_csv_connection_ids', JSON.stringify(csvConnectionIds));
            }
          }
        }

        setDisableEin(true);
        formatOrg.terms = true;
        setCompanyData(formatOrg);

        toast({
          title: "Organization loaded",
          description: "You can now edit this organization's information."
        });
      }
    } catch (error) {
      console.error('Error loading organization by ID:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveDraft = async (
    data: CompanyData,
    currentSession: Session | null = session,
    submissionIdentifier: string = 'default'
  ) => {
    const emailToUse = data.pocEmail || '';
    if (!emailToUse) {
      toast({
        title: "Error",
        description: "An email is required to save your progress",
        variant: "destructive"
      });
      return false;
    }

    const dataToSave: CompanyData = {
      ...data,
      products: Array.isArray(data.products) ? data.products : parseProducts(data.products as unknown as Json)
    };

    // Try to get organization_id from multiple sources
    let organizationId = data.organization_id || companyData?.organization_id;

    // Fallback: get from sessionStorage
    if (!organizationId) {
      const savedData = sessionStorage.getItem('figabl_company_data');
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          organizationId = parsedData.organization_id;
        } catch (error) {
          console.error('Error parsing sessionStorage for organization_id:', error);
        }
      }
    }

    // Fallback: check if there's an existing draft organization with same EIN + email
    if (!organizationId && dataToSave.ein && emailToUse) {
      try {
        const { data: existingOrg } = await supabase
          .from('organisations')
          .select('id')
          .eq('ein', dataToSave.ein)
          .eq('poc_email', emailToUse)
          .eq('status', 'draft')
          .maybeSingle();

        if (existingOrg?.id) {
          organizationId = existingOrg.id;
        }
      } catch (error) {
        console.error('Error checking for existing draft organization:', error);
      }
    }

    // Set the organization_id if we found one
    if (organizationId) {
      dataToSave.organization_id = organizationId;
      // Persist to sessionStorage for future navigation
      const currentSessionData = sessionStorage.getItem('figabl_company_data');
      if (currentSessionData) {
        try {
          const parsedSessionData = JSON.parse(currentSessionData);
          parsedSessionData.organization_id = organizationId;
          sessionStorage.setItem('figabl_company_data', JSON.stringify(parsedSessionData));
        } catch (error) {
          console.error('Error updating sessionStorage with organization_id:', error);
        }
      }
    }

    // Ensure connections is always an array
    if (!dataToSave.connections) {
      dataToSave.connections = [];
    }

    const storedConnections = sessionStorage.getItem('figabl_connection_ids');
    const connectionIds = storedConnections ? JSON.parse(storedConnections) : [];

    const storedCsvConnections = sessionStorage.getItem('figabl_csv_connection_ids');
    const csvConnectionIds = storedCsvConnections ? JSON.parse(storedCsvConnections) : [];

    const legacyConnectionId = sessionStorage.getItem('figabl_connection_id');
    if (legacyConnectionId && !connectionIds.includes(legacyConnectionId)) {
      connectionIds.push(legacyConnectionId);
    }

    if (csvConnectionIds && Array.isArray(csvConnectionIds)) {
      csvConnectionIds.forEach(csvConnectionId => {
        if (!connectionIds.includes(csvConnectionId)) {
          connectionIds.push(csvConnectionId);
        }
      });
    }

    if (connectionIds.length > 0) {
      connectionIds.forEach(connectionId => {
        if (connectionId && !dataToSave.connections?.some(conn => conn.id === connectionId)) {
          let connectionType = 'other';
          if (connectionId.startsWith('csv-import-')) {
            connectionType = 'csv';
          }

          const connection: Connection = {
            id: connectionId,
            type: connectionType,
            name: connectionType === 'csv' ? 'CSV Import' : 'Data Connection',
            status: 'active',
            created_at: new Date().toISOString()
          };

          dataToSave.connections.push(connection);
        }
      });
    }

    // Update magic_link_sessions if we have an email but no session
    if (!currentSession?.user?.id) {
      try {
        // Use edge function to save draft (anonymous)
        const organizationData = {
          poc_name: dataToSave.pocName,
          poc_email: dataToSave.pocEmail,
          poc_phone: dataToSave.pocPhone || null,
          poc_phone_verified: dataToSave.pocPhoneVerified || false,
          name: dataToSave.name,
          name_dbas: dataToSave.name_dbas || null,
          address: dataToSave.address || '',
          state_of_incorporation: dataToSave.stateOfIncorporation || '',
          ein: dataToSave.ein || '',
          products: dataToSave.products as unknown as Json,
          connections: (Array.isArray(dataToSave.connections) ? dataToSave.connections.map(normalizeConnectionForStorage) : []) as unknown as Json,
          revenue_last_12_months: dataToSave.revenueLast12Months || null,
          status: 'draft' as const,
        };

        const { data: fnRes, error: fnErr } = await invokeFunction(supabase, 'organisations-manager-v2', {
          body: {
            action: 'save_draft',
            data: organizationData,
            organization_id: dataToSave.organization_id || null,
          }
        });

        if (fnErr || !fnRes?.success) {
          console.error('Edge function error (save_draft anon):', fnErr || fnRes);
          throw fnErr || new Error(fnRes?.error || 'Failed to save draft');
        }

        if (fnRes.organization_id) {
          dataToSave.organization_id = fnRes.organization_id;
          // Persist to sessionStorage for future navigation
          const currentSessionData = sessionStorage.getItem('figabl_company_data');
          if (currentSessionData) {
            try {
              const parsedSessionData = JSON.parse(currentSessionData);
              parsedSessionData.organization_id = fnRes.organization_id;
              sessionStorage.setItem('figabl_company_data', JSON.stringify(parsedSessionData));
            } catch (error) {
              console.error('Error updating sessionStorage with organization_id:', error);
            }
          }
        }

        // Generate or use existing submission identifier
        let finalSubmissionIdentifier = submissionIdentifier;

        if (submissionIdentifier === 'default') {
          // Generate a stable identifier for this email
          finalSubmissionIdentifier = generateStableIdentifier(emailToUse);
          // Store it in sessionStorage for consistency
          sessionStorage.setItem('figabl_submission_identifier', finalSubmissionIdentifier);
        }

        // Store submission info in magic_link_sessions table
        const { data: existingSession, error: sessionFetchError } = await supabase
          .from('magic_link_sessions')
          .select('*')
          .eq('email', emailToUse)
          .eq('submission_identifier', finalSubmissionIdentifier)
          .maybeSingle();

        if (sessionFetchError && sessionFetchError.code !== 'PGRST116') {
          console.error('Error checking existing magic link session:', sessionFetchError);
        }

        if (!existingSession) {
          const { data, error: sessionError } = await invokeFunction(supabase, 'update-magic-link-session', {
            body: {
              action: 'insert',
              sessionData: {
                email: emailToUse,
                submission_identifier: finalSubmissionIdentifier,
                organisation_id: dataToSave.organization_id || null
              }
            }
          });

          if (sessionError || !data?.success) {
            console.error('Error creating magic link session:', sessionError?.message || data?.error);
          }
        }

        dataToSave.terms = true
        setCompanyData(dataToSave);
        toast({
          title: "Progress saved",
          description: "Your information has been saved."
        });

        return true;
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to save your progress. Please try again.",
          variant: "destructive"
        });
        return false;
      }
    }

    try {
      // Generate or use existing submission identifier for authenticated users too
      let finalSubmissionIdentifier = submissionIdentifier;

      if (submissionIdentifier === 'default') {
        finalSubmissionIdentifier = generateStableIdentifier(emailToUse);
        sessionStorage.setItem('figabl_submission_identifier', finalSubmissionIdentifier);
      }

      // Update magic_link_sessions if we have a user
      const { data: existingSession, error: sessionFetchError } = await supabase
        .from('magic_link_sessions')
        .select('*')
        .eq('email', currentSession.user.email || emailToUse)
        .eq('submission_identifier', finalSubmissionIdentifier)
        .maybeSingle();

      if (sessionFetchError && sessionFetchError.code !== 'PGRST116') {
        console.error('Error checking existing magic link session:', sessionFetchError);
      }

      if (!existingSession) {
        const { data, error: sessionError } = await invokeFunction(supabase, 'update-magic-link-session', {
          body: {
            action: 'insert',
            sessionData: {
              email: currentSession.user.email || emailToUse,
              submission_identifier: finalSubmissionIdentifier,
              organisation_id: dataToSave.organization_id || null
            }
          }
        });

        if (sessionError || !data?.success) {
          console.error('Error creating magic link session:', sessionError?.message || data?.error);
        }
      }

      // Use edge function to save draft (authenticated)
      const organizationData = {
        poc_name: dataToSave.pocName,
        poc_email: dataToSave.pocEmail,
        poc_phone: dataToSave.pocPhone || null,
        poc_phone_verified: dataToSave.pocPhoneVerified || false,
        name: dataToSave.name,
        name_dbas: dataToSave.name_dbas || null,
        address: dataToSave.address || '',
        state_of_incorporation: dataToSave.stateOfIncorporation || '',
        ein: dataToSave.ein || '',
        products: dataToSave.products as unknown as Json,
        connections: dataToSave.connections as unknown as Json,
        revenue_last_12_months: dataToSave.revenueLast12Months || null,
        status: 'draft' as const,
      };

      const { data: fnRes, error: fnErr } = await invokeFunction(supabase, 'organisations-manager-v2', {
        body: {
          action: 'save_draft',
          data: organizationData,
          organization_id: dataToSave.organization_id || null,
        }
      });

      if (fnErr || !fnRes?.success) {
        console.error('Edge function error (save_draft auth):', fnErr || fnRes);
        throw fnErr || new Error(fnRes?.error || 'Failed to save draft');
      }

      if (fnRes.organization_id) {
        dataToSave.organization_id = fnRes.organization_id;
        setCompanyData(dataToSave);
      }

      dataToSave.terms = true
      setCompanyData(dataToSave);
      toast({
        title: "Progress saved",
        description: "Your information has been saved. You can continue later."
      });

      return true;
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save your progress. Please try again.",
        variant: "destructive"
      });
      return false;
    }
  };

  const linkAnonymousDraft = async (
    userId: string,
    userEmail: string,
    submissionIdentifier: string = 'default'
  ) => {
    try {
      // Check if we have a specific submission for this email
      const { data: sessionData, error: sessionLookupError } = await supabase
        .from('magic_link_sessions')
        .select('*')
        .eq('email', userEmail)
        .eq('submission_identifier', submissionIdentifier)
        .maybeSingle();

      if (sessionLookupError && sessionLookupError.code !== 'PGRST116') {
        console.error('Error looking up magic link session:', sessionLookupError);
      }

      const { data: orgs, error: orgError } = await supabase
        .from('organisations')
        .select('*')
        .eq('poc_email', userEmail)
        .order('created_at', { ascending: false });

      if (orgError) {
        throw orgError;
      }

      if (orgs && orgs.length > 0) {
        const org = orgs[0];
        if (!org.products || !Array.isArray(org.products)) {
          org.products = parseProducts(org.products) as unknown as Json[];
        }

        // update user id
        const { error } = await supabase
          .from('organisations')
          .update({ user_id: userId })
          .eq('id', org.id);

        const formatOrg: CompanyData = {
          user_id: userId,
          pocName: org.poc_name,
          pocEmail: org.poc_email,
          pocPhone: org.poc_phone || '',
          pocPhoneVerified: org.poc_phone_verified || false,
          name: org.name,
          name_dbas: org.name_dbas,
          address: org.address,
          stateOfIncorporation: org.state_of_incorporation,
          ein: org.ein,
          revenueLast12Months: org.revenue_last_12_months || undefined,
          products: org.products as any,
          connections: parseConnections(org.connections),
          organization_id: org.id
        };

        // Normalize connections from organization data
        if (org.connections) {
          sessionStorage.removeItem('figabl_connection_ids');
          sessionStorage.removeItem('figabl_connection_id');
          sessionStorage.removeItem('figabl_csv_connection_id');
          sessionStorage.removeItem('figabl_csv_connection_ids');

          if (formatOrg.connections.length > 0) {
            const connectionIds = formatOrg.connections.map(conn => conn.id);

            const csvConnectionIds = connectionIds.filter(id => id.startsWith('csv-import-'));
            const nonCsvConnectionIds = connectionIds.filter(id => !id.startsWith('csv-import-'));

            if (nonCsvConnectionIds.length > 0) {
              sessionStorage.setItem('figabl_connection_ids', JSON.stringify(nonCsvConnectionIds));
              sessionStorage.setItem('figabl_connection_id', nonCsvConnectionIds[0]);
            }

            if (csvConnectionIds.length > 0) {
              sessionStorage.setItem('figabl_csv_connection_ids', JSON.stringify(csvConnectionIds));
            }
          }
        }

        formatOrg.terms = true
        setCompanyData(formatOrg);

        toast({
          title: "Company loaded",
          description: "Your most recent progress has been restored."
        });
        return;
      }
    } catch (error) {
      return null;
    }
  };

  // Modified loadSavedProgress to use the submission identifier from URL or sessionStorage
  const loadSavedProgress = async (
    userId: string,
    userEmail?: string | null,
    explicitSubmissionIdentifier?: string
  ) => {
    // Use explicit submissionIdentifier if provided, otherwise get from URL/sessionStorage
    const submissionIdentifier = explicitSubmissionIdentifier || getSubmissionIdentifierFromURL();

    try {
      if (userEmail) {
        // First check if the authenticated email is an alternative email
        const { data: altSessionData, error: altSessionError } = await (supabase
          .rpc as any)('find_session_by_alternative_email', { search_email: userEmail });

        if (altSessionError) {
          console.error('Error checking alternative email:', altSessionError);
        }

        // Find the matching session with the right submission identifier
        const hasAlternativeEmailMatch = Array.isArray(altSessionData) && altSessionData.length > 0;
        const alternativeEmailData = hasAlternativeEmailMatch
          ? altSessionData.find(session => session.submission_identifier === submissionIdentifier)
          : null;

        // If user logged in with alternative email, load the organization associated with that email
        if (alternativeEmailData?.organisation_id) {
          await loadOrganisationById(alternativeEmailData.organisation_id, submissionIdentifier);
          return;
        }

        // Continue with normal flow - try to link anonymous draft
        const linkedDraft = await linkAnonymousDraft(userId, userEmail, submissionIdentifier);
        if (linkedDraft) {
          linkedDraft.terms = true;
          setCompanyData(linkedDraft);
          return;
        }
      }

      // Load drafts from organisations table where status='draft'
      const { data: orgs, error: orgError } = await supabase
        .from('organisations')
        .select('*')
        .or(`user_id.eq.${userId},poc_email.eq.${userEmail}`)
        .eq('status', 'draft')
        .order('created_at', { ascending: false });

      if (orgError) {
        throw orgError;
      }

      if (orgs && orgs.length > 0) {
        const org = orgs[0];


        // update user id if needed
        if (!org.user_id) {
          const { error } = await supabase
            .from('organisations')
            .update({ user_id: userId })
            .eq('id', org.id);
        }

        const formatOrg: CompanyData = {
          user_id: userId,
          pocName: org.poc_name,
          pocEmail: org.poc_email,
          pocPhone: org.poc_phone || '',
          pocPhoneVerified: org.poc_phone_verified || false,
          name: org.name,
          name_dbas: org.name_dbas,
          address: org.address,
          stateOfIncorporation: org.state_of_incorporation,
          ein: org.ein,
          revenueLast12Months: org.revenue_last_12_months || undefined,
          products: parseProducts(org.products),
          connections: parseConnections(org.connections),
          organization_id: org.id
        };

        // Normalize connections from organization data
        if (org.connections) {
          sessionStorage.removeItem('figabl_connection_ids');
          sessionStorage.removeItem('figabl_connection_id');
          sessionStorage.removeItem('figabl_csv_connection_id');
          sessionStorage.removeItem('figabl_csv_connection_ids');

          if (formatOrg.connections.length > 0) {
            const connectionIds = formatOrg.connections.map(conn => conn.id);

            const csvConnectionIds = connectionIds.filter(id => id.startsWith('csv-import-'));
            const nonCsvConnectionIds = connectionIds.filter(id => !id.startsWith('csv-import-'));

            if (nonCsvConnectionIds.length > 0) {
              sessionStorage.setItem('figabl_connection_ids', JSON.stringify(nonCsvConnectionIds));
              sessionStorage.setItem('figabl_connection_id', nonCsvConnectionIds[0]);
            }

            if (csvConnectionIds.length > 0) {
              sessionStorage.setItem('figabl_csv_connection_ids', JSON.stringify(csvConnectionIds));
            }
          }
        }

        setDisableEin(true)
        formatOrg.terms = true
        setCompanyData(formatOrg);

        toast({
          title: "Draft loaded",
          description: "Your most recent progress has been restored."
        });
        return;
      }

      // Also check for submitted organizations (for users who want to edit)
      const { data: submittedOrgs, error: submittedOrgError } = await supabase
        .from('organisations')
        .select('*')
        .or(`user_id.eq.${userId},poc_email.eq.${userEmail}`)
        .eq('status', 'submitted')
        .order('created_at', { ascending: false });

      if (submittedOrgError) {
        throw submittedOrgError;
      }

      if (submittedOrgs && submittedOrgs.length > 0) {
        const org = submittedOrgs[0];


        // update user id if needed
        if (!org.user_id) {
          await supabase
            .from('organisations')
            .update({ user_id: userId })
            .eq('id', org.id);
        }

        const formatOrg: CompanyData = {
          user_id: userId,
          pocName: org.poc_name,
          pocEmail: org.poc_email,
          pocPhone: org.poc_phone || '',
          pocPhoneVerified: org.poc_phone_verified || false,
          name: org.name,
          name_dbas: org.name_dbas,
          address: org.address,
          stateOfIncorporation: org.state_of_incorporation,
          ein: org.ein,
          revenueLast12Months: org.revenue_last_12_months || undefined,
          products: parseProducts(org.products),
          connections: parseConnections(org.connections),
          organization_id: org.id
        };

        // Normalize connections from organization data
        if (org.connections) {
          sessionStorage.removeItem('figabl_connection_ids');
          sessionStorage.removeItem('figabl_connection_id');
          sessionStorage.removeItem('figabl_csv_connection_id');
          sessionStorage.removeItem('figabl_csv_connection_ids');

          if (formatOrg.connections.length > 0) {
            const connectionIds = formatOrg.connections.map(conn => conn.id);

            const csvConnectionIds = connectionIds.filter(id => id.startsWith('csv-import-'));
            const nonCsvConnectionIds = connectionIds.filter(id => !id.startsWith('csv-import-'));

            if (nonCsvConnectionIds.length > 0) {
              sessionStorage.setItem('figabl_connection_ids', JSON.stringify(nonCsvConnectionIds));
              sessionStorage.setItem('figabl_connection_id', nonCsvConnectionIds[0]);
            }

            if (csvConnectionIds.length > 0) {
              sessionStorage.setItem('figabl_csv_connection_ids', JSON.stringify(csvConnectionIds));
            }
          }
        }

        setDisableEin(true)
        formatOrg.terms = true
        setCompanyData(formatOrg);

        toast({
          title: "Organization loaded",
          description: "Your most recent submission has been restored."
        });
        return;
      }

      try {
        const { data: submissions, error: submissionError } = await supabase
          .from('form_submissions')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false });

        if (submissionError) {
          throw submissionError;
        }

        if (submissions && submissions.length > 0) {
          const mostRecentSubmission = submissions[0];
          const submissionData = mostRecentSubmission.company_data as unknown as CompanyData;

          if (!submissionData.products || !Array.isArray(submissionData.products)) {
            submissionData.products = submissionData.products || [];
          }

          // Ensure phone verification status is preserved from submission data
          if (!submissionData.pocPhone) {
            submissionData.pocPhone = '';
          }
          if (submissionData.pocPhoneVerified === undefined) {
            submissionData.pocPhoneVerified = false;
          }

          // Normalize connections from submission data
          if (submissionData.connections && !Array.isArray(submissionData.connections)) {
            const connectionsArray: Connection[] = [];
            if (submissionData.connections && typeof submissionData.connections === 'object') {
              for (const key in submissionData.connections as any) {
                const conn = (submissionData.connections as any)[key];
                if (conn) {
                  connectionsArray.push({
                    ...conn,
                    id: key
                  });
                }
              }
            }
            submissionData.connections = connectionsArray;
          } else if (!submissionData.connections) {
            submissionData.connections = [];
          }

          submissionData.terms = true
          setCompanyData(submissionData);
          toast({
            title: "Previous submission loaded",
            description: "You can review and update your information."
          });
        }
      } catch (error) {
        console.error(error)
        // Silently handle errors
      }
    } catch (error) {
      if (error instanceof Error && error.message !== 'No rows returned') {
        console.error("An unexpected error occurred while loading your progress.", error);
      }
    }
  };

  const loadDraftById = async (organizationId: string) => {
    try {
      setIsLoading(true);
      const { data: org, error } = await supabase
        .from('organisations')
        .select('*')
        .eq('id', organizationId)
        .single();

      if (error) {
        throw error;
      }

      if (org) {
        const formatOrg: CompanyData = {
          user_id: org.user_id,
          pocName: org.poc_name,
          pocEmail: org.poc_email,
          pocPhone: org.poc_phone || '',
          pocPhoneVerified: org.poc_phone_verified || false,
          name: org.name,
          name_dbas: org.name_dbas || '',
          address: org.address,
          stateOfIncorporation: org.state_of_incorporation,
          ein: org.ein,
          revenueLast12Months: org.revenue_last_12_months || undefined,
          products: parseProducts(org.products),
          connections: parseConnections(org.connections),
          organization_id: organizationId
        };

        formatOrg.terms = true
        setCompanyData(formatOrg);

        sessionStorage.setItem('figabl_company_data', JSON.stringify(formatOrg));

        toast({
          title: "Organization loaded",
          description: "You are now editing this organization."
        });

        if (formatOrg.connections && formatOrg.connections.length > 0) {
          const connectionIds = formatOrg.connections.map(conn => conn.id);

          sessionStorage.removeItem('figabl_connection_ids');
          sessionStorage.removeItem('figabl_connection_id');
          sessionStorage.removeItem('figabl_csv_connection_id');
          sessionStorage.removeItem('figabl_csv_connection_ids');

          const csvConnectionIds = connectionIds.filter(id => id.startsWith('csv-import-'));
          const nonCsvConnectionIds = connectionIds.filter(id => !id.startsWith('csv-import-'));

          if (nonCsvConnectionIds.length > 0) {
            sessionStorage.setItem('figabl_connection_ids', JSON.stringify(nonCsvConnectionIds));
            sessionStorage.setItem('figabl_connection_id', nonCsvConnectionIds[0]);
          }
          if (csvConnectionIds.length > 0) {
            sessionStorage.setItem('figabl_csv_connection_ids', JSON.stringify(csvConnectionIds));
          }
        }
      }
    } catch (error) {
      console.error('Error loading organization by ID:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const checkInitialSession = async () => {
      try {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const hasAuthInHash = hashParams.has('access_token') || hashParams.has('refresh_token');
        const token = getTokenFromURL();
        const submissionIdentifier = getSubmissionIdentifierFromURL();

        if (hasAuthInHash || token) {
          setTokenDetected(true);
          setIsVerifyingToken(true);

          if (token) {
            await verifyMagicLink(token);
          } else {
            const { data, error } = await supabase.auth.getSession();
            if (error) {
              throw error;
            }

            if (data.session) {
              setSession(data.session);
              await loadSavedProgress(data.session.user.id, data.session.user.email, submissionIdentifier);
            } else {
              throw new Error('Authentication failed. Please try logging in again.');
            }
          }

          return;
        }

        const { data: { session: initialSession } } = await supabase.auth.getSession();

        if (initialSession?.user) {
          setSession(initialSession);

          // Enhanced handling for session without token
          const userEmail = initialSession.user.email;
          const submissionIdentifier = getSubmissionIdentifierFromURL();

          // First check if the authenticated email is an alternative email
          if (userEmail) {
            const { data: altSessionData, error: altSessionError } = await (supabase
              .rpc as any)('find_session_by_alternative_email', { search_email: userEmail });

            if (altSessionError) {
              console.error('Error checking alternative email:', altSessionError);
            }

            // Find the matching session with the right submission identifier
            const hasAlternativeEmailMatch = Array.isArray(altSessionData) && altSessionData.length > 0;
            const alternativeEmailData = hasAlternativeEmailMatch
              ? altSessionData.find(session => session.submission_identifier === submissionIdentifier)
              : null;

            // If user logged in with alternative email, load the organization associated with that email
            if (alternativeEmailData?.organisation_id) {
              await loadOrganisationById(alternativeEmailData.organisation_id, submissionIdentifier);
              setIsLoading(false);
              return;
            }
          }

          // Continue with regular flow
          await loadSavedProgress(initialSession.user.id, initialSession.user.email, submissionIdentifier);
        }

        setIsLoading(false);
      } catch (error) {
        toast({
          title: "Session error",
          description: "There was an issue loading your session",
          variant: "destructive"
        });
        setIsLoading(false);
      }
    };

    if (!isVerifyingToken) {
      checkInitialSession();
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (isVerifyingToken) {
        return;
      }

      if (event === 'SIGNED_IN' && newSession?.user) {
        setSession(newSession);

        setTimeout(async () => {
          const submissionIdentifier = getSubmissionIdentifierFromURL();

          if (pendingSave) {
            await saveDraft(pendingSave, newSession, submissionIdentifier);
            setPendingSave(null);
          }

          // Check if this is an alternative email
          if (newSession.user.email) {
            const { data: altSessionData, error: altSessionError } = await (supabase
              .rpc as any)('find_session_by_alternative_email', { search_email: newSession.user.email });

            if (altSessionError) {
              console.error('Error checking alternative email:', altSessionError);
            }

            // Find the matching session with the right submission identifier
            const hasAlternativeEmailMatch = Array.isArray(altSessionData) && altSessionData.length > 0;
            const alternativeEmailData = hasAlternativeEmailMatch
              ? altSessionData.find(session => session.submission_identifier === submissionIdentifier)
              : null;

            if (alternativeEmailData?.organisation_id) {
              await loadOrganisationById(alternativeEmailData.organisation_id, submissionIdentifier);
              setIsLoading(false);
              return;
            }
          }

          await loadSavedProgress(newSession.user.id, newSession.user.email, submissionIdentifier);
          setIsLoading(false);
        }, 0);
      } else if (event === 'SIGNED_OUT') {
        setSession(null);
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [isVerifyingToken, pendingSave, location.search]);

  return {
    companyData,
    setCompanyData,
    formCompleted,
    setFormCompleted,
    session,
    pendingSave,
    setPendingSave,
    isLoading,
    isVerifyingToken,
    saveDraft,
    tokenDetected,
    loadDraftById,
    loadOrganisationById,
    disableEin
  };
};
